import React, { useState } from "react";
import { useSEO } from "../hooks/useSEO";

const AWARDS = [
  {
    bloc: "Bloc I — Les Prix de la Performance",
    items: [
      {
        title: "Spectacle de l'Année",
        subtitle: "Le trophée suprême. Récompense le show qui a mis tout le monde d'accord sur la scène nationale.",
        eligibility: [
          "Tournée active d'au moins 20 dates sur la période, ou création dans une salle de 1 000 places minimum",
          "Spectacle solo uniquement (les duos et troupes relèvent de leur catégorie dédiée)",
        ],
        criteria: [
          { label: "Cohérence thématique et arc narratif du spectacle", poids: "30%" },
          { label: "Rapport texte / jeu de scène / énergie live", poids: "30%" },
          { label: "Réception critique (presse spécialisée + bouche-à-oreille documenté)", poids: "20%" },
          { label: "Impact culturel de l'année (citations, reprises, mèmes issus du show)", poids: "20%" },
        ],
      },
      {
        title: "Humoriste de l'Année — Homme & Femme",
        subtitle: "Récompense la présence scénique, l'aura globale et le rayonnement sur l'année. Deux trophées distincts.",
        eligibility: [
          "Spectacle en cours de tournée sur la période",
          "Au moins 2 présences médias (TV nationale, radio nationale, presse écrite) sur la période",
        ],
        criteria: [
          { label: "Présence scénique et singularité d'univers", poids: "30%" },
          { label: "Constance sur la durée de la tournée", poids: "20%" },
          { label: "Rayonnement médiatique et pop culture", poids: "25%" },
          { label: "Évolution artistique par rapport à l'année précédente", poids: "25%" },
        ],
      },
      {
        title: "Révélation de l'Année",
        subtitle: "Le prix « pépite ». Pour celui ou celle qui a percé ces 12 derniers mois.",
        eligibility: [
          "Premier spectacle solo sorti sur la période, OU première tournée nationale après un premier album de scène",
          "N'a jamais été nominé·e dans une catégorie majeure d'une cérémonie nationale auparavant",
        ],
        criteria: [
          { label: "Originalité de la proposition par rapport au marché existant", poids: "35%" },
          { label: "Taux de remplissage rapide des salles", poids: "20%" },
          { label: "Couverture spontanée (sans RP) sur les réseaux et la presse", poids: "20%" },
          { label: "Potentiel à long terme jugé par le jury", poids: "25%" },
        ],
      },
      {
        title: "Meilleur Duo ou Troupe",
        subtitle: "Pour saluer le travail collectif. L'identité commune prime sur la somme des individus.",
        eligibility: [
          "Groupe de 2 artistes minimum avec un spectacle collectif sorti sur la période",
          "Le spectacle doit être conçu comme une œuvre collective (pas un « best of » de solos côte à côte)",
        ],
        criteria: [
          { label: "Complémentarité et alchimie entre les membres", poids: "35%" },
          { label: "Identité commune reconnaissable (au-delà de la somme des solos)", poids: "35%" },
          { label: "Qualité scénique collective vs. concurrence du marché solo", poids: "30%" },
        ],
      },
    ],
  },
  {
    bloc: "Bloc II — Les Prix de la Création",
    items: [
      {
        title: "Meilleure Écriture / Meilleur Texte",
        subtitle: "Pour les puristes. Récompense la finesse des punchlines, la structure et le travail sur la langue.",
        eligibility: [
          "Spectacle ou texte publié/diffusé sur la période",
          "Soumis par l'auteur, un producteur, ou sélectionné par la rédaction",
        ],
        criteria: [
          { label: "Densité des punchlines (ratio punchline / minute)", poids: "25%" },
          { label: "Construction du propos : montée en puissance, chute maîtrisée", poids: "30%" },
          { label: "Originalité thématique et traitement du sujet", poids: "25%" },
          { label: "Travail sur la langue : rythme, jeux de mots, subversion", poids: "20%" },
        ],
      },
      {
        title: "Prix de l'Improvisation",
        subtitle: "Indispensable à l'ère des interactions public. Pour ceux qui brillent sans filet.",
        eligibility: [
          "Artiste ayant une section d'improvisation documentée dans son spectacle (minimum 10 minutes)",
          "OU participant régulier à des formats live d'improvisation (au moins 5 dates sur la période)",
        ],
        criteria: [
          { label: "Vitesse et justesse de la réaction", poids: "35%" },
          { label: "Capacité à intégrer le public sans perdre le fil", poids: "35%" },
          { label: "Mémorabilité des moments improvisés (captés ou témoignés)", poids: "30%" },
        ],
        note: "Toute note doit s'appuyer sur une captation vidéo ou une vision live. Les évaluations par ouï-dire ne sont pas acceptées.",
      },
      {
        title: "Meilleure Mise en Scène",
        subtitle: "Pour les spectacles qui ont une vraie proposition visuelle et artistique. L'humour comme objet scénique total.",
        eligibility: [
          "Spectacle avec un metteur en scène crédité",
          "Proposition visuelle distincte et documentée : scénographie, lumières, vidéo, costume",
        ],
        criteria: [
          { label: "Cohérence entre la mise en scène et le propos du texte", poids: "40%" },
          { label: "Audace et innovation scénographique", poids: "35%" },
          { label: "Maîtrise des silences, des rythmes et de l'espace", poids: "25%" },
        ],
      },
    ],
  },
  {
    bloc: "Bloc III — Les Nouveaux Formats",
    items: [
      {
        title: "Meilleure Chronique Média",
        subtitle: "Pour ceux qui brillent à la radio ou à la TV avec constance et originalité.",
        eligibility: [
          "Chronique régulière sur une antenne nationale (radio ou TV) ou un service de streaming reconnu",
          "Minimum 20 interventions / épisodes sur la période de référence",
        ],
        criteria: [
          { label: "Consistance sur la durée (pas uniquement les meilleurs moments)", poids: "35%" },
          { label: "Originalité de l'angle vs. les autres chroniqueurs de l'antenne", poids: "35%" },
          { label: "Impact sur la conversation publique (citations presse, réactions)", poids: "30%" },
        ],
      },
      {
        title: "Prix du Contenu Digital",
        subtitle: "Pour le meilleur sketch ou format humour sur Instagram, TikTok ou YouTube.",
        eligibility: [
          "Compte actif sur au moins 1 plateforme",
          "Au moins 10 publications originales sur la période",
          "L'un des critères suivants est rempli : (a) ≥ 50 000 abonnés, (b) un contenu ayant dépassé 1M de vues, (c) sélection coup de cœur de la rédaction",
        ],
        criteria: [
          { label: "Qualité d'écriture et originalité du format court", poids: "35%" },
          { label: "Régularité et cohérence de la ligne éditoriale", poids: "25%" },
          { label: "Engagement qualitatif (commentaires, partages) — pas seulement les vues", poids: "25%" },
          { label: "Passage éventuel du digital vers la scène ou les médias traditionnels", poids: "15%" },
        ],
      },
      {
        title: "Meilleur Podcast d'Humour",
        subtitle: "Un format qui explose et qui mérite son propre trophée.",
        eligibility: [
          "Podcast actif avec au moins 10 épisodes publiés sur la période",
          "Disponible sur au moins 2 plateformes de distribution majeures (Spotify, Apple Podcasts, Deezer, etc.)",
        ],
        criteria: [
          { label: "Identité sonore et qualité de production", poids: "30%" },
          { label: "Pertinence et originalité du concept", poids: "40%" },
          { label: "Fidélité d'audience (taux de complétion, croissance abonnements)", poids: "30%" },
        ],
      },
    ],
  },
  {
    bloc: "Bloc IV — Les Prix Spéciaux",
    items: [
      {
        title: "Le Rire d'Honneur",
        subtitle: "Pour l'ensemble de la carrière d'une légende. Décerné par le jury seul, sans candidature.",
        eligibility: [
          "Carrière d'au moins 15 ans dans l'humour francophone",
          "Impact reconnu par les pairs et documenté (spectacles, œuvres, influence sur la génération suivante)",
          "Décerné par le jury uniquement — pas de candidature possible, pas de vote public",
        ],
        criteria: [
          { label: "Longévité et renouvellement artistique au fil des années", poids: "—" },
          { label: "Influence sur la génération d'humoristes suivante", poids: "—" },
          { label: "Œuvre documentée et accessible (spectacles, livres, films, séries)", poids: "—" },
        ],
        note: "Ce prix peut ne pas être décerné certaines années si le jury estime qu'aucun profil ne s'impose clairement.",
      },
      {
        title: "Prix du Public",
        subtitle: "La seule catégorie où les gens votent directement pour leur chouchou.",
        eligibility: [
          "Ouvert à tout artiste ayant une activité publique sur la période",
          "La shortlist de 5 noms est établie par la rédaction — le vote public choisit parmi ces 5",
        ],
        criteria: [
          { label: "Vote en ligne ouvert pendant 3 semaines avant la cérémonie", poids: "100%" },
          { label: "1 vote par adresse email vérifiée", poids: "—" },
          { label: "Résultats détaillés publiés le soir de la cérémonie", poids: "—" },
        ],
      },
      {
        title: "Humour Noir",
        subtitle: "Pour saluer ceux qui osent explorer le sombre, le macabre et le transgressif avec maîtrise.",
        eligibility: [
          "Spectacle ou contenu avec un propos clairement teinté d'humour noir, macabre ou transgressif assumé",
          "Soumis par l'artiste/producteur ou sélectionné directement par le jury",
        ],
        criteria: [
          { label: "Maîtrise du curseur : transgresser sans brutaliser gratuitement", poids: "35%" },
          { label: "Cohérence de l'univers noir sur l'ensemble de l'œuvre", poids: "35%" },
          { label: "Présence effective du rire (vs. simple choc ou malaise)", poids: "30%" },
        ],
        note: "Distinction avec Humour Engagé : ce prix récompense un univers esthétique, pas une intention politique.",
      },
      {
        title: "Humour Engagé",
        subtitle: "Pour ceux qui utilisent le rire comme arme critique, avec courage et sans autocensure.",
        eligibility: [
          "Spectacle ou contenu dont le propos politique ou social est central — pas juste une chronique d'actualité",
          "L'humour doit être l'outil de déconstruction, pas seulement le véhicule",
        ],
        criteria: [
          { label: "Clarté et force du propos sans tomber dans le militantisme pur", poids: "35%" },
          { label: "Usage de l'humour comme outil de déconstruction", poids: "35%" },
          { label: "Courage : sujets difficiles traités sans autocensure excessive", poids: "30%" },
        ],
        note: "Distinction avec Humour Noir : ce prix récompense une intention politique, pas un esthétisme sombre.",
      },
    ],
  },
];

function AwardCard({ award, open, onToggle }) {
  return (
    <div style={{ borderTop: "1px solid #1c1c1c" }}>
      <button
        className="w-full text-left py-10 flex items-start justify-between gap-8 group"
        onClick={onToggle}
      >
        <div>
          <h3
            className="font-serif transition-colors duration-500"
            style={{ fontSize: "clamp(28px, 3.5vw, 52px)", lineHeight: 1.05, color: open ? "#C9A961" : "#F8F6F0" }}
          >
            {award.title}
          </h3>
          <p className="mt-3 font-serif italic" style={{ fontSize: 18, color: "rgba(248,246,240,0.65)", lineHeight: 1.5 }}>
            {award.subtitle}
          </p>
        </div>
        <span className="label mt-2 shrink-0" style={{ color: "#C9A961", fontSize: 20 }}>
          {open ? "−" : "+"}
        </span>
      </button>

      {open && (
        <div className="pb-14 space-y-12">
          {/* Eligibility */}
          <div>
            <p className="label mb-6" style={{ color: "#A1A1AA" }}>Éligibilité</p>
            <ul className="space-y-3">
              {award.eligibility.map((line, i) => (
                <li key={i} className="flex gap-4">
                  <span style={{ color: "#C9A961", marginTop: 3 }}>—</span>
                  <span style={{ color: "rgba(248,246,240,0.8)", fontSize: 16, lineHeight: 1.7 }}>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Criteria table */}
          <div>
            <p className="label mb-6" style={{ color: "#A1A1AA" }}>Critères d'évaluation</p>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #2A2724" }}>
                  <th className="label text-left pb-4" style={{ color: "#A1A1AA", fontWeight: 400 }}>Critère</th>
                  <th className="label text-right pb-4" style={{ color: "#A1A1AA", fontWeight: 400, width: 80 }}>Poids</th>
                </tr>
              </thead>
              <tbody>
                {award.criteria.map((c, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #1c1c1c" }}>
                    <td style={{ padding: "14px 0", color: "rgba(248,246,240,0.8)", fontSize: 16, lineHeight: 1.5 }}>{c.label}</td>
                    <td className="label text-right" style={{ color: "#C9A961", paddingLeft: 16 }}>{c.poids}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {award.note && (
            <p className="font-serif italic" style={{ color: "rgba(248,246,240,0.5)", fontSize: 15, lineHeight: 1.7 }}>
              {award.note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Trophees() {
  useSEO("Trophées", "Les 13 distinctions du Stand d'Or. Critères, éligibilité et barème pour chaque prix de l'humour francophone.");

  const [openKey, setOpenKey] = useState(null);

  const toggle = (key) => setOpenKey(openKey === key ? null : key);

  return (
    <div className="pt-[160px] pb-40 max-w-[1400px] mx-auto px-6 lg:px-12" data-testid="trophees-page">
      <p className="label mb-8" style={{ color: "#C9A961" }}>Édition MMXXVI</p>
      <h1
        className="font-serif"
        style={{ fontSize: "clamp(56px, 10vw, 156px)", lineHeight: 0.92, letterSpacing: "-0.03em", color: "#F8F6F0" }}
        data-testid="trophees-title"
      >
        Les Trophées
      </h1>
      <p
        className="mt-10 max-w-2xl"
        style={{ color: "rgba(248,246,240,0.72)", fontSize: 18, lineHeight: 1.7 }}
      >
        Treize prix, quatre blocs thématiques. Chaque trophée est défini par des critères d'éligibilité précis
        et une grille d'évaluation pondérée — parce que l'excellence mérite d'être mesurée avec rigueur.
      </p>

      <div className="mt-24 space-y-20">
        {AWARDS.map((bloc) => (
          <section key={bloc.bloc}>
            <p className="label mb-12" style={{ color: "#A1A1AA", letterSpacing: "0.1em" }}>{bloc.bloc}</p>
            <div>
              {bloc.items.map((award) => {
                const key = `${bloc.bloc}-${award.title}`;
                return (
                  <AwardCard
                    key={key}
                    award={award}
                    open={openKey === key}
                    onToggle={() => toggle(key)}
                  />
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
